import { useCallback, useRef } from 'react';
import messagesService from '../services/messages';
import { playAdminChime } from '../helpers/adminChime';
import useBackgroundPoll from '../helpers/useBackgroundPoll';

/**
 * Watches customer support conversations for the whole dashboard.
 *
 * Two jobs, both of which used to require someone to already be sitting on the
 * Chat page:
 *
 *  - **A count for the sidebar** — how many threads have a message nobody here
 *    has answered. Published on `window` plus a DOM event, the same way
 *    `NotificationBar` publishes its unread counts, so the sidebar can render a
 *    badge without a second request of its own.
 *  - **A sound when a new thread opens.** A customer starting a chat is the one
 *    admin event with a person waiting at the other end of it, and a silent
 *    badge in an unfocused tab is not an answer to that.
 *
 * Polled rather than subscribed. The conversations live in Firestore, but the
 * dashboard reaches them through the backend (`/messages/counts`) which reads
 * them with server credentials — the admin browser has no Firestore access to
 * that collection, so a client-side `onSnapshot` here would only ever fail
 * silently.
 *
 * Mounted once, high in the tree, so both keep working from any page.
 */

export const ADMIN_CHAT_COUNTS_EVENT = 'ks:admin-chat-counts';

/**
 * How often to ask while somebody is looking.
 *
 * This used to fetch the entire inbox every twenty seconds, forever — a
 * dashboard left open in a tab all day was hundreds of thousands of billed
 * Firestore reads against a fifty-thousand daily allowance, which is what
 * eventually put "Quota exceeded" on the client's screen. Two changes fix the
 * arithmetic: a counts endpoint costing two reads instead of two hundred, and
 * a slower timer in a background tab.
 *
 * A minute is well inside the time it takes anyone to notice a new chat, and
 * a tab brought back to the front refreshes immediately rather than waiting.
 */
const POLL_MS = 60000;

/** In a background tab. Slower, but never silent: a waiting customer still rings. */
const BACKGROUND_POLL_MS = 180000;

function publish(counts) {
  window.__ksAdminChatCounts = counts;
  window.dispatchEvent(new CustomEvent(ADMIN_CHAT_COUNTS_EVENT, { detail: counts }));
}

export default function AdminChatWatch() {
  /**
   * How many threads the previous poll saw.
   *
   * `null` until the first response lands, which is what stops the entire
   * existing backlog announcing itself the moment the dashboard loads. Only a
   * thread that appears *after* that counts as new — and since a customer
   * opening a chat creates a conversation, the total going up is exactly that
   * event. It used to compare sets of thread ids, which meant fetching every
   * thread to learn one number.
   */
  const knownTotal = useRef(null);

  const poll = useCallback(async () => {
    try {
      const res = await messagesService.getConversationCounts();
      const data = res?.data ?? {};
      // Firestore unreachable (quota, misconfiguration): hold the last known
      // numbers rather than redrawing the badge as zero.
      if (data.degraded) return;

      const total = Number(data.total) || 0;
      const waiting = Number(data.waiting) || 0;

      if (knownTotal.current != null && total > knownTotal.current) playAdminChime('chat');
      knownTotal.current = total;

      publish({ total, waiting });
    } catch {
      // A failed poll leaves the badge on its last known value rather than
      // flashing zero — the count is a hint, and a wrong hint is worse than a
      // stale one.
    }
  }, []);

  useBackgroundPoll(poll, { visibleMs: POLL_MS, hiddenMs: BACKGROUND_POLL_MS });

  return null;
}
